import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowLeft, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function useClipboard(text, ms = 1800) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), ms);
    });
  };
  return [copied, copy];
}

function CopyBtn({ text, label = "Copy" }) {
  const [copied, copy] = useClipboard(text);
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-all select-none"
      style={{
        borderColor: copied ? "#22c55e" : "rgba(255,255,255,0.15)",
        color: copied ? "#22c55e" : "rgba(255,255,255,0.6)",
        background: copied ? "rgba(34,197,94,0.08)" : "transparent",
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

function Badge({ method }) {
  const colors = {
    POST: { bg: "#0f3460", text: "#60a5fa", border: "#1e40af" },
    GET:  { bg: "#042f2e", text: "#34d399", border: "#065f46" },
    PATCH:{ bg: "#3b1f00", text: "#fb923c", border: "#7c2d12" },
    DELETE:{ bg: "#3b0f0f", text: "#f87171", border: "#991b1b" },
  };
  const c = colors[method] || colors.GET;
  return (
    <span
      className="text-xs font-bold px-2 py-0.5 rounded-md uppercase tracking-wider"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
    >
      {method}
    </span>
  );
}

function CodeBlock({ code, language = "bash" }) {
  return (
    <div className="relative rounded-xl overflow-hidden" style={{ background: "#0d1117", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <span className="text-xs font-medium uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>{language}</span>
        <CopyBtn text={code} />
      </div>
      <pre className="px-5 py-4 text-sm overflow-x-auto leading-relaxed" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', 'Fira Code', Menlo, monospace", margin: 0 }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-14 scroll-mt-24">
      <h2 className="text-xl font-bold mb-5" style={{ color: "#f1f5f9", fontFamily: "'Work Sans', sans-serif", letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function FieldRow({ name, type, required, children }) {
  return (
    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <td className="py-3 pr-4 align-top">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-sm font-bold" style={{ color: "#93c5fd" }}>{name}</code>
          {required && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.25)" }}>required</span>
          )}
        </div>
      </td>
      <td className="py-3 pr-4 align-top">
        <code className="text-xs" style={{ color: "#a78bfa" }}>{type}</code>
      </td>
      <td className="py-3 align-top text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{children}</td>
    </tr>
  );
}

function ErrorRow({ code, name, desc }) {
  const colors = {
    "400": "#fb923c", "401": "#f87171", "402": "#fbbf24",
    "409": "#a78bfa", "422": "#60a5fa", "500": "#f87171",
  };
  return (
    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <td className="py-3 pr-4 align-top">
        <span className="text-sm font-bold" style={{ color: colors[String(code)] || "#e2e8f0" }}>{code}</span>
      </td>
      <td className="py-3 pr-4 align-top">
        <code className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>{name}</code>
      </td>
      <td className="py-3 align-top text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{desc}</td>
    </tr>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { id: "overview",              label: "Overview" },
  { id: "authentication",        label: "Authentication" },
  { id: "base-url",              label: "Base URL" },
  { id: "rate-limits",           label: "Rate Limits" },
  { id: "endpoint-create",       label: "POST /customers" },
  { id: "endpoint-get",          label: "GET /customers/:id" },
  { id: "endpoint-packages",     label: "GET /customers/:id/packages" },
  { id: "endpoint-invoices",     label: "GET /customers/:id/invoices" },
  { id: "endpoint-rates",        label: "GET /rates" },
  { id: "endpoint-subscribers",  label: "POST /subscribers" },
  { id: "endpoint-unsubscribe",  label: "DELETE /subscribers/:email" },
  { id: "endpoint-tenants",      label: "POST /tenants" },
  { id: "errors",                label: "Error Reference" },
  { id: "get-key",               label: "Getting an API Key" },
];

const CURL_CREATE = `curl -X POST https://kanal.cyrusfieldtech.com/api/partner/v1/customers \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE" \\
  -d '{
    "email": "alice@example.com",
    "name": "Alice Brown",
    "phone": "+18765551234",
    "country": "JM",
    "address": "12 Hope Road, Kingston 6, Jamaica",
    "trn": "123456789"
  }'`;

const RESPONSE_200 = `{
  "ok": true,
  "customer_number": "SW460329",
  "user_id": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "email": "alice@example.com",
  "already_existed": false
}`;

const RESPONSE_409 = `{
  "detail": "Email already registered for this tenant"
}`;

const RESPONSE_401 = `{
  "detail": "Invalid or revoked partner API key"
}`;

const RESPONSE_402 = `{
  "detail": "Owning tenant account is locked — resolve outstanding invoices to re-enable partner API access"
}`;

const RESPONSE_422 = `{
  "detail": [
    {
      "loc": ["body", "email"],
      "msg": "value is not a valid email address",
      "type": "value_error.email"
    }
  ]
}`;

// ── GET /customers/:identifier ─────────────────────────────────────────────────
const CURL_GET_BY_EMAIL = `curl https://kanal.cyrusfieldtech.com/api/partner/v1/customers/alice%40example.com \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const CURL_GET_BY_CUSNUM = `curl https://kanal.cyrusfieldtech.com/api/partner/v1/customers/SW460329 \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const RESPONSE_GET_CUSTOMER = `{
  "user_id": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "email": "alice@example.com",
  "name": "Alice Brown",
  "phone": "+18765551234",
  "customer_number": "SW460329",
  "country": "JM",
  "address": "12 Hope Road, Kingston 6, Jamaica",
  "trn": "123456789",
  "account_status": "active",
  "email_verified": true,
  "created_at": "2026-01-15T14:30:00Z"
}`;

// ── GET /customers/:identifier/packages ───────────────────────────────────────
const CURL_PACKAGES = `curl "https://kanal.cyrusfieldtech.com/api/partner/v1/customers/SW460329/packages?limit=10&offset=0" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const CURL_PACKAGES_FILTERED = `curl "https://kanal.cyrusfieldtech.com/api/partner/v1/customers/alice%40example.com/packages?status=delivered&limit=5" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

// ── GET /customers/:identifier/invoices ───────────────────────────────────────
const CURL_INVOICES = `curl "https://kanal.cyrusfieldtech.com/api/partner/v1/customers/SW460329/invoices?limit=10&offset=0" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const CURL_INVOICES_FILTERED = `curl "https://kanal.cyrusfieldtech.com/api/partner/v1/customers/alice%40example.com/invoices?status=unpaid" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const RESPONSE_INVOICES = `{
  "total": 2,
  "limit": 10,
  "offset": 0,
  "items": [
    {
      "invoice_id": "b2c3d4e5-0001-0000-0000-000000000001",
      "invoice_number": "INV-00042",
      "customer_name": "Alice Brown",
      "customer_email": "alice@example.com",
      "amount": 1750.00,
      "currency": "JMD",
      "status": "unpaid",
      "due_date": "2026-09-20T00:00:00Z",
      "paid_at": null,
      "payment_method": null,
      "package_id": "a1b2c3d4-0001-0000-0000-000000000001",
      "notes": "Air shipment 2 lbs",
      "created_at": "2026-09-07T10:00:00Z"
    },
    {
      "invoice_id": "b2c3d4e5-0002-0000-0000-000000000002",
      "invoice_number": "INV-00038",
      "customer_name": "Alice Brown",
      "customer_email": "alice@example.com",
      "amount": 3850.00,
      "currency": "JMD",
      "status": "paid",
      "due_date": "2026-08-15T00:00:00Z",
      "paid_at": "2026-08-12T14:30:00Z",
      "payment_method": "wipay",
      "package_id": "a1b2c3d4-0002-0000-0000-000000000002",
      "notes": "Air shipment 5 lbs",
      "created_at": "2026-08-08T09:00:00Z"
    }
  ]
}`;

// ── POST /subscribers ─────────────────────────────────────────────────────────
const CURL_SUBSCRIBERS = `curl -X POST https://kanal.cyrusfieldtech.com/api/partner/v1/subscribers \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE" \\
  -d '{
    "email": "alice@example.com",
    "name": "Alice Brown",
    "source": "kyoown_platform"
  }'`;

const RESPONSE_SUBSCRIBERS_201 = `{
  "ok": true,
  "resubscribed": false
}`;

const RESPONSE_SUBSCRIBERS_RESUB = `{
  "ok": true,
  "resubscribed": true
}`;

// ── DELETE /subscribers/{email} ───────────────────────────────────────────────
const CURL_UNSUBSCRIBE = `curl -X DELETE "https://kanal.cyrusfieldtech.com/api/partner/v1/subscribers/alice%40example.com" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const RESPONSE_UNSUBSCRIBE_200 = `{
  "ok": true,
  "unsubscribed": true
}`;

// ── POST /tenants ─────────────────────────────────────────────────────────────
const CURL_TENANTS = `curl -X POST https://kanal.cyrusfieldtech.com/api/partner/v1/tenants \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE" \\
  -d '{
    "company_name": "KYo Couriers Ltd",
    "admin_name": "Marcus Reid",
    "admin_email": "marcus@kyocouriers.com",
    "admin_password": "secure-temp-pass",
    "phone": "+1-876-555-0100",
    "address": "12 Harbour St, Kingston",
    "country": "JM",
    "plan": "starter"
  }'`;

const RESPONSE_TENANTS_201 = `{
  "ok": true,
  "company_id": "a1b2c3d4-...",
  "company_name": "KYo Couriers Ltd",
  "admin_email": "marcus@kyocouriers.com",
  "admin_user_id": "u1u2u3u4-...",
  "public_slug": "ky-7f3a12",
  "role": "company_admin",
  "account_locked": false,
  "email_verified": false,
  "verification_required": true,
  "customer_signup_url": "https://kanal.cyrusfieldtech.com/c/ky-7f3a12/signup",
  "created_at": "2026-10-02T00:00:00Z",
  "message": "Tenant created. The admin must verify their email before logging in."
}`;

// ── GET /rates ────────────────────────────────────────────────────────────────
const CURL_RATES = `curl https://kanal.cyrusfieldtech.com/api/partner/v1/rates \\
  -H "Authorization: Bearer kanal_pk_YOUR_KEY_HERE"`;

const RESPONSE_RATES = `{
  "currency": "JMD",
  "per_lb_rate": 600,
  "rate_table": [
    { "lbs": 1,   "price": 600 },
    { "lbs": 2,   "price": 1000 },
    { "lbs": 3,   "price": 1400 },
    { "lbs": 4,   "price": 1750 },
    { "lbs": 5,   "price": 2100 },
    ...
    { "lbs": 100, "price": 35350 }
  ]
}`;

const RESPONSE_PACKAGES = `{
  "total": 2,
  "limit": 10,
  "offset": 0,
  "items": [
    {
      "package_id": "a1b2c3d4-0001-0000-0000-000000000001",
      "tracking_number": "KN1029384756",
      "status": "delivered",
      "shipment_method": "air",
      "description": "Electronics",
      "weight_kg": 1.2,
      "sender_name": "Cyber Store Ltd",
      "sender_address": "11420 Fortune Cir Unit 34, Wellington FL 33414",
      "created_at": "2026-01-10T09:00:00Z",
      "updated_at": "2026-01-18T14:22:00Z",
      "delivered_at": "2026-01-18T14:22:00Z"
    },
    {
      "package_id": "a1b2c3d4-0002-0000-0000-000000000002",
      "tracking_number": "KN9876543210",
      "status": "in_transit",
      "shipment_method": "sea",
      "description": "Clothing",
      "weight_kg": 3.5,
      "sender_name": "Fashion Depot",
      "sender_address": "200 Fashion Ave, New York NY 10001",
      "created_at": "2026-01-20T11:00:00Z",
      "updated_at": "2026-01-22T08:00:00Z",
      "delivered_at": null
    }
  ]
}`;

// ─── Component ────────────────────────────────────────────────────────────────
export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState("overview");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { threshold: 0.3, rootMargin: "-80px 0px -60% 0px" }
    );
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileNavOpen(false);
  };

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── Top Nav ─────────────────────────────────────────────────────────── */}
      <header style={{ background: "rgba(10,15,26,0.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.07)", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 group">
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" style={{ color: "rgba(255,255,255,0.4)" }} />
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>Back</span>
            </Link>
            <span style={{ color: "rgba(255,255,255,0.15)" }}>|</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: "#f1f5f9", fontFamily: "'Work Sans', sans-serif" }}>Kanal</span>
              <span className="text-sm" style={{ color: "rgba(255,255,255,0.3)" }}>Partner API</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: "rgba(0,43,246,0.25)", color: "#60a5fa", border: "1px solid rgba(0,43,246,0.4)" }}>v1</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="mailto:dev@cyrusfieldtech.com"
              className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.12)" }}
            >
              <ExternalLink size={12} />
              Request Access
            </a>
            {/* Mobile nav toggle */}
            <button
              className="sm:hidden flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
              style={{ border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)" }}
              onClick={() => setMobileNavOpen((v) => !v)}
            >
              Sections {mobileNavOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {mobileNavOpen && (
          <div className="sm:hidden px-4 pb-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            {NAV_SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="block w-full text-left py-2 text-sm px-2 rounded"
                style={{ color: activeSection === id ? "#60a5fa" : "rgba(255,255,255,0.5)" }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex gap-10">

        {/* ── Sidebar ───────────────────────────────────────────────────────── */}
        <aside className="hidden sm:block shrink-0 w-52">
          <div className="sticky top-24">
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "rgba(255,255,255,0.3)" }}>Contents</p>
            <nav className="space-y-0.5">
              {NAV_SECTIONS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="block w-full text-left px-3 py-1.5 rounded-lg text-sm transition-all"
                  style={{
                    color: activeSection === id ? "#60a5fa" : "rgba(255,255,255,0.45)",
                    background: activeSection === id ? "rgba(96,165,250,0.1)" : "transparent",
                    borderLeft: activeSection === id ? "2px solid #60a5fa" : "2px solid transparent",
                    fontWeight: activeSection === id ? "500" : "400",
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div className="mt-8 p-3 rounded-xl text-xs leading-relaxed" style={{ background: "rgba(0,43,246,0.1)", border: "1px solid rgba(0,43,246,0.25)", color: "rgba(255,255,255,0.5)" }}>
              Need help? Email<br />
              <a href="mailto:dev@cyrusfieldtech.com" className="underline" style={{ color: "#60a5fa" }}>dev@cyrusfieldtech.com</a>
            </div>
          </div>
        </aside>

        {/* ── Main ──────────────────────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 max-w-3xl">

          {/* Hero */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#60a5fa" }}>Partner API</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.25)" }}>
                Live
              </span>
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ color: "#f8fafc", fontFamily: "'Work Sans', sans-serif", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
              Kanal Partner API
            </h1>
            <p className="text-base leading-relaxed max-w-xl" style={{ color: "rgba(255,255,255,0.55)" }}>
              Server-to-server REST API for partner storefronts to provision Kanal customer accounts.
              Integrate with your signup flow so your customers land in Kanal instantly — no manual steps.
            </p>
          </div>

          {/* Overview */}
          <Section id="overview" title="Overview">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {[
                { label: "Current version", value: "v1" },
                { label: "Protocol", value: "HTTPS / REST" },
                { label: "Auth scheme", value: "Bearer Token" },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <p className="text-xs mb-1" style={{ color: "rgba(255,255,255,0.35)" }}>{label}</p>
                  <p className="text-sm font-semibold" style={{ color: "#f1f5f9" }}>{value}</p>
                </div>
              ))}
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
              The Kanal Partner API lets trusted integrators create customer accounts on behalf of a
              courier company. Every API key is scoped to a single Kanal tenant. Accounts created via
              the API receive a branded welcome email with their temporary password, and immediately
              appear in the courier's customer list with a unique customer number.
            </p>
          </Section>

          {/* Base URL */}
          <Section id="base-url" title="Base URL">
            <div className="rounded-xl overflow-hidden" style={{ background: "#0d1117", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="text-xs font-medium uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>base url</span>
                <CopyBtn text="https://kanal.cyrusfieldtech.com/api" />
              </div>
              <div className="px-5 py-4">
                <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>
                  <span style={{ color: "rgba(255,255,255,0.3)" }}>https://</span>
                  <span style={{ color: "#93c5fd" }}>kanal.cyrusfieldtech.com</span>
                  <span style={{ color: "#a78bfa" }}>/api</span>
                </code>
              </div>
            </div>
            <p className="text-sm mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
              All Partner API paths are relative to this base. For example, the customer creation
              endpoint is at <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#a78bfa" }}>/api/partner/v1/customers</code>.
            </p>
          </Section>

          {/* Authentication */}
          <Section id="authentication" title="Authentication">
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              All Partner API requests require a <strong style={{ color: "#e2e8f0" }}>Bearer token</strong> in the{" "}
              <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>Authorization</code> header.
              Tokens are issued by your Kanal super-admin from the Companies page and follow the format{" "}
              <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#a78bfa" }}>kanal_pk_XXXXXXXXXX.YYYYYY</code>.
            </p>
            <CodeBlock language="http" code={`Authorization: Bearer kanal_pk_YOUR_KEY_HERE`} />
            <div className="mt-4 p-4 rounded-xl text-sm leading-relaxed" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "rgba(255,255,255,0.55)" }}>
              <strong style={{ color: "#fca5a5" }}>Keep your key secret.</strong>{" "}
              Treat it like a password — never expose it in client-side code or public repos.
              Revoke and rotate keys immediately if they are compromised. A super-admin can
              revoke any key from the Kanal dashboard.
            </div>
          </Section>

          {/* Rate Limits */}
          <Section id="rate-limits" title="Rate Limits">
            <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
              Partner API requests share the per-tenant rate limit tier. Limits apply per rolling minute window:
            </p>
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Plan</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Requests / minute</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { plan: "Starter", rpm: "60" },
                    { plan: "Pro",     rpm: "300" },
                    { plan: "Scale",   rpm: "1,000" },
                  ].map(({ plan, rpm }) => (
                    <tr key={plan} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td className="px-4 py-3 text-sm" style={{ color: "#e2e8f0" }}>{plan}</td>
                      <td className="px-4 py-3 text-sm font-mono" style={{ color: "#4ade80" }}>{rpm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.35)" }}>
              Exceeding the rate limit returns <code className="px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#fb923c" }}>429 Too Many Requests</code>.
            </p>
          </Section>

          {/* Endpoint */}
          <Section id="endpoint-create" title="Create Customer">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="POST" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/customers</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Provisions a new <strong style={{ color: "#e2e8f0" }}>customer</strong> account under the tenant associated with your API key.
              The new customer receives a branded welcome email with their temporary password and a link
              to log in. A unique <strong style={{ color: "#e2e8f0" }}>customer number</strong> is auto-assigned and returned for use in
              shipment bookings.
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Request body</h3>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <FieldRow name="email" type="string (email)" required>Valid email address. Must be unique within this courier's tenant. Used as the customer's login.</FieldRow>
                  <FieldRow name="name" type="string" required>Customer's full name. Min 1 character.</FieldRow>
                  <FieldRow name="country" type="string (ISO-2)" required>ISO 3166-1 alpha-2 country code (e.g. <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>JM</code>, <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>US</code>). Defaults to <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>JM</code>.</FieldRow>
                  <FieldRow name="phone" type="string">Customer's phone number including country code (e.g. <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>+18765551234</code>).</FieldRow>
                  <FieldRow name="address" type="string">Customer's shipping address. Stored as the default address on their recipient profile.</FieldRow>
                  <FieldRow name="trn" type="string">Jamaica Tax Registration Number — exactly 9 digits (dashes allowed: <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>123-456-789</code>). Only accepted when <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>country=JM</code>.</FieldRow>
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example request</h3>
            <CodeBlock language="curl" code={CURL_CREATE} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_200} />
            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "ok", d: "Always true on success." },
                    { f: "customer_number", d: "Stable, auto-assigned identifier for this customer within the courier (e.g. SW460329). Use this when booking shipments." },
                    { f: "user_id", d: "Internal UUID for the newly created user." },
                    { f: "email", d: "Lowercased, normalised email as stored." },
                    { f: "already_existed", d: "Always false for a successful creation. Reserved for future idempotency behavior." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* GET customer profile */}
          <Section id="endpoint-get" title="Get Customer Profile">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="GET" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/customers/<span style={{ color: "#a78bfa" }}>{"{identifier}"}</span></code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Retrieve a customer's account and recipient profile. The{" "}
              <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#a78bfa" }}>identifier</code>{" "}
              path segment accepts either an <strong style={{ color: "#e2e8f0" }}>email address</strong> or a{" "}
              <strong style={{ color: "#e2e8f0" }}>customer number</strong> (returned from the create endpoint).
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Lookup by email</h3>
            <CodeBlock language="curl" code={CURL_GET_BY_EMAIL} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Lookup by customer number</h3>
            <CodeBlock language="curl" code={CURL_GET_BY_CUSNUM} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_GET_CUSTOMER} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "user_id",        d: "UUID of the customer's Kanal login account. null if the customer has packages but no login." },
                    { f: "email",          d: "Customer's email address (lowercased)." },
                    { f: "name",           d: "Customer's display name." },
                    { f: "phone",          d: "Phone number, or null if not provided." },
                    { f: "customer_number",d: "Stable alphanumeric identifier (e.g. SW460329). Use this in shipment bookings." },
                    { f: "country",        d: "ISO-2 country code recorded at signup." },
                    { f: "address",        d: "Default shipping address, or null." },
                    { f: "trn",            d: "Jamaica TRN (9 digits), or null. Returned unmasked to the issuing partner." },
                    { f: "account_status", d: <>One of <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>active</code>, <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>pending_verification</code>, <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>blocked</code>, or <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>recipient_only</code> (shipment record exists but no login account).</> },
                    { f: "email_verified", d: "true once the customer has clicked the welcome email link." },
                    { f: "created_at",     d: "ISO 8601 timestamp of account creation." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* GET packages */}
          <Section id="endpoint-packages" title="List Customer Packages">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="GET" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/customers/<span style={{ color: "#a78bfa" }}>{"{identifier}"}</span>/packages</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Returns a paginated list of all shipments associated with the customer, ordered newest first.
              Accepts the same email-or-customer-number <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#a78bfa" }}>identifier</code> as the profile endpoint.
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Query parameters</h3>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Param</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { p: "limit",  t: "integer", d: "Page size — 1 to 100. Default: 20." },
                    { p: "offset", t: "integer", d: "Number of records to skip. Default: 0." },
                    { p: "status", t: "string",  d: <>Filter to a specific status: <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>pending</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>in_transit</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>ready_for_pickup</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>delivered</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>cancelled</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>return_to_sender</code></> },
                  ].map(({ p, t, d }) => (
                    <tr key={p} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "#0d1117" }}>
                      <td className="px-4 py-3 align-top"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{p}</code></td>
                      <td className="px-4 py-3 align-top"><code className="text-xs" style={{ color: "#a78bfa" }}>{t}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example — all packages by customer number</h3>
            <CodeBlock language="curl" code={CURL_PACKAGES} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example — delivered packages by email</h3>
            <CodeBlock language="curl" code={CURL_PACKAGES_FILTERED} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_PACKAGES} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "total",           d: "Total count of matching packages (before pagination)." },
                    { f: "limit / offset",  d: "Echo of the pagination params supplied in the request." },
                    { f: "items[].package_id",       d: "Internal UUID of the package." },
                    { f: "items[].tracking_number",  d: "KN-prefixed tracking code (e.g. KN1029384756). Use for public tracking at /c/{slug}/track/{number}." },
                    { f: "items[].status",           d: "Current delivery status." },
                    { f: "items[].shipment_method",  d: "air · sea · ground." },
                    { f: "items[].weight_kg",        d: "Package weight in kilograms." },
                    { f: "items[].sender_name",      d: "Name of the US/origin sender or store." },
                    { f: "items[].delivered_at",     d: "ISO 8601 delivery timestamp, or null if not yet delivered." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* GET invoices */}
          <Section id="endpoint-invoices" title="List Customer Invoices">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="GET" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/customers/<span style={{ color: "#a78bfa" }}>{"{identifier}"}</span>/invoices</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Returns a paginated list of invoices for the given customer, ordered newest first.
              Accepts the same email-or-customer-number <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#a78bfa" }}>identifier</code> as the profile endpoint.
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Query parameters</h3>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Param</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { p: "limit",  t: "integer", d: "Page size — 1 to 100. Default: 20." },
                    { p: "offset", t: "integer", d: "Records to skip. Default: 0." },
                    { p: "status", t: "string",  d: <><code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>unpaid</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>paid</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>overdue</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>cancelled</code></> },
                  ].map(({ p, t, d }) => (
                    <tr key={p} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "#0d1117" }}>
                      <td className="px-4 py-3 align-top"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{p}</code></td>
                      <td className="px-4 py-3 align-top"><code className="text-xs" style={{ color: "#a78bfa" }}>{t}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example — all invoices by customer number</h3>
            <CodeBlock language="curl" code={CURL_INVOICES} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example — unpaid invoices by email</h3>
            <CodeBlock language="curl" code={CURL_INVOICES_FILTERED} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_INVOICES} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "invoice_id",      d: "Internal UUID of the invoice." },
                    { f: "invoice_number",  d: "Human-readable reference displayed on the invoice PDF (e.g. INV-00042)." },
                    { f: "customer_name",   d: "Name as recorded on the invoice." },
                    { f: "customer_email",  d: "Email the invoice is addressed to." },
                    { f: "amount",          d: "Total invoice amount in the stated currency." },
                    { f: "currency",        d: "ISO 4217 currency code (e.g. JMD, USD)." },
                    { f: "status",          d: <>One of <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>unpaid</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>paid</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>overdue</code> · <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#a78bfa" }}>cancelled</code>.</> },
                    { f: "due_date",        d: "ISO 8601 payment due date, or null." },
                    { f: "paid_at",         d: "ISO 8601 timestamp when payment was confirmed, or null." },
                    { f: "payment_method",  d: "e.g. wipay · stripe · cash · bank_transfer, or null if unpaid." },
                    { f: "package_id",      d: "UUID of the linked package, or null for standalone invoices." },
                    { f: "notes",           d: "Free-text note added by the dispatcher, or null." },
                    { f: "created_at",      d: "ISO 8601 invoice creation timestamp." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* GET rates */}
          <Section id="endpoint-rates" title="Get Shipping Rates">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="GET" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/rates</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Returns the courier's live shipping rate table authenticated with your partner key —
              no customer JWT required. Use this to display up-to-date rates on your storefront.
              The response always reflects the latest rates as configured by the courier admin.
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example request</h3>
            <CodeBlock language="curl" code={CURL_RATES} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_RATES} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "currency",      d: "ISO 4217 currency code for all prices in this response (e.g. JMD, USD)." },
                    { f: "per_lb_rate",   d: "Base rate for 1 lb — convenience field matching rate_table[0].price." },
                    { f: "rate_table",    d: "Ordered array of weight tiers, each with lbs (integer) and price (number in the stated currency). Use lbs as an exact lookup key for the package weight." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-4 rounded-xl text-sm leading-relaxed" style={{ background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.2)", color: "rgba(255,255,255,0.55)" }}>
              <strong style={{ color: "#93c5fd" }}>Rate lookup example (JavaScript):</strong>
              <pre className="mt-2 text-xs overflow-x-auto" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{`const { rate_table, currency } = await fetch('/api/partner/v1/rates', {
  headers: { Authorization: \`Bearer \${KANAL_PARTNER_KEY}\` }
}).then(r => r.json());

const tier = rate_table.find(t => t.lbs === packageWeightLbs);
const price = tier?.price ?? null; // null = weight not in table`}</pre>
            </div>
          </Section>

          {/* POST /subscribers */}
          <Section id="endpoint-subscribers" title="Add Newsletter Subscriber">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="POST" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/subscribers</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Adds an email address to the courier's newsletter subscriber list (visible in the
              Communication → Subscribers tab). Idempotent — re-submitting an address that was
              previously unsubscribed will reactivate it; submitting an already-active address
              returns <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>ok: true</code> silently.
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Request body</h3>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Required</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "email",  t: "string",  r: "Yes", d: "Valid email address. Stored in lowercase." },
                    { f: "name",   t: "string",  r: "No",  d: "Subscriber's display name. Shown in the dashboard." },
                    { f: "source", t: "string",  r: "No",  d: `Origin label (e.g. "kyoown_platform", "mobile_app"). Defaults to "partner".` },
                  ].map(({ f, t, r, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 align-top text-sm" style={{ color: "rgba(255,255,255,0.55)" }}>{t}</td>
                      <td className="px-4 py-3 align-top text-sm" style={{ color: r === "Yes" ? "#f87171" : "rgba(255,255,255,0.35)" }}>{r}</td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example request</h3>
            <CodeBlock language="curl" code={CURL_SUBSCRIBERS} />

            <h3 className="text-sm font-semibold mt-6 mb-2 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 201 Created (new subscriber)</h3>
            <CodeBlock language="json" code={RESPONSE_SUBSCRIBERS_201} />

            <h3 className="text-sm font-semibold mt-6 mb-2 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 201 (reactivated unsubscribed email)</h3>
            <CodeBlock language="json" code={RESPONSE_SUBSCRIBERS_RESUB} />
          </Section>

          {/* DELETE /subscribers/:email */}
          <Section id="endpoint-unsubscribe" title="Unsubscribe an Email">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="DELETE" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/subscribers/<span style={{ color: "#a78bfa" }}>{"{email}"}</span></code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Marks a subscriber as inactive so they no longer appear in the active subscriber
              list and are excluded from future campaigns. Idempotent — calling on a
              non-existent or already-inactive address returns{" "}
              <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>ok: true</code> silently.
              URL-encode the email address (replace <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>@</code> with <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>%40</code>).
            </p>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example request</h3>
            <CodeBlock language="curl" code={CURL_UNSUBSCRIBE} />

            <h3 className="text-sm font-semibold mt-6 mb-2 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 200 OK</h3>
            <CodeBlock language="json" code={RESPONSE_UNSUBSCRIBE_200} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "ok",           d: "Always true when the request was well-formed and authorised." },
                    { f: "unsubscribed", d: "true if the record was just deactivated; false if it was already inactive or not found." },
                    { f: "reason",       d: 'Omitted on success. "already_inactive" or "not_found" when unsubscribed is false.' },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* POST /tenants */}
          <Section id="endpoint-tenants" title="Create a Courier Tenant">
            <div className="flex items-center gap-3 mb-5">
              <Badge method="POST" />
              <code className="text-sm" style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>/partner/v1/tenants</code>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
              Registers a new courier company (tenant) and provisions its first{" "}
              <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>company_admin</code> account.
              Designed for platforms that onboard couriers externally (e.g. KYoown) and need a
              matching Kanal tenant created immediately after their own signup flow.
              A verification email is dispatched to the admin's inbox — the tenant can log in
              once the email address is confirmed.
            </p>
            <div className="rounded-xl p-4 mb-6 text-sm" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", color: "rgba(251,191,36,0.9)" }}>
              <strong>Note:</strong> No subscription invoice is generated for partner-created tenants —
              billing is handled by the calling platform. The tenant account is active immediately
              (not locked) so staff can log in as soon as they verify their email.
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Request body</h3>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Required</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "company_name",    t: "string",  r: "Yes", d: "Display name for the courier company. Must be unique across the platform." },
                    { f: "admin_name",      t: "string",  r: "Yes", d: "Full name of the company admin account to provision." },
                    { f: "admin_email",     t: "string",  r: "Yes", d: "Email address for the admin account. Must not already exist on the platform." },
                    { f: "admin_password",  t: "string",  r: "Yes", d: "Initial password for the admin account. Minimum 6 characters." },
                    { f: "phone",           t: "string",  r: "No",  d: "Company contact phone number." },
                    { f: "address",         t: "string",  r: "No",  d: "Company street address." },
                    { f: "country",         t: "string",  r: "No",  d: 'ISO 3166-1 alpha-2 country code. Defaults to "JM" (Jamaica).' },
                    { f: "plan",            t: "string",  r: "No",  d: 'Subscription plan: "starter", "pro", or "scale". Defaults to "starter".' },
                    { f: "website",         t: "string",  r: "No",  d: "Company website URL." },
                  ].map(({ f, t, r, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 align-top text-sm" style={{ color: "rgba(255,255,255,0.55)" }}>{t}</td>
                      <td className="px-4 py-3 align-top text-sm" style={{ color: r === "Yes" ? "#f87171" : "rgba(255,255,255,0.35)" }}>{r}</td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Example request</h3>
            <CodeBlock language="curl" code={CURL_TENANTS} />

            <h3 className="text-sm font-semibold mt-6 mb-2 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>Response — 201 Created</h3>
            <CodeBlock language="json" code={RESPONSE_TENANTS_201} />

            <div className="rounded-xl overflow-hidden mt-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Field</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Description</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#0d1117" }}>
                  {[
                    { f: "company_id",            d: "UUID of the newly created tenant company." },
                    { f: "admin_user_id",          d: "UUID of the provisioned company_admin user account." },
                    { f: "public_slug",            d: "URL-safe slug for the tenant's public customer signup page." },
                    { f: "customer_signup_url",    d: "Full URL customers can use to self-register with this courier." },
                    { f: "verification_required",  d: "Always true. The admin must click the verification link in their inbox before logging in." },
                  ].map(({ f, d }) => (
                    <tr key={f} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td className="px-4 py-3 align-top whitespace-nowrap"><code className="text-sm" style={{ color: "#93c5fd", fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>{f}</code></td>
                      <td className="px-4 py-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Error Reference */}
          <Section id="errors" title="Error Reference">            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              All errors return JSON with a <code className="text-xs px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "#93c5fd" }}>detail</code> field.
              Standard HTTP status codes apply.
            </p>
            <div className="rounded-xl overflow-hidden mb-6" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full" style={{ background: "#0d1117" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Code</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.35)" }}>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <ErrorRow code="400" name="Bad Request" desc="Validation failed — e.g., TRN provided without country=JM, or country code not exactly 2 letters." />
                  <ErrorRow code="401" name="Unauthorized" desc="Missing, malformed, or revoked API key." />
                  <ErrorRow code="402" name="Payment Required" desc="The owning courier's subscription invoice is overdue. Resolve outstanding invoices to re-enable API access." />
                  <ErrorRow code="409" name="Conflict" desc="A customer with this email already exists in the courier's tenant." />
                  <ErrorRow code="422" name="Unprocessable Entity" desc="Request body failed Pydantic schema validation (invalid email format, missing required field, etc.)." />
                  <ErrorRow code="429" name="Too Many Requests" desc="Per-tenant rate limit exceeded. Retry after the rolling window resets (~60 seconds)." />
                  <ErrorRow code="500" name="Internal Server Error" desc="Unexpected server error. Contact support if this persists." />
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>409 — Duplicate email</h3>
            <CodeBlock language="json" code={RESPONSE_409} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>401 — Invalid key</h3>
            <CodeBlock language="json" code={RESPONSE_401} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>402 — Locked tenant</h3>
            <CodeBlock language="json" code={RESPONSE_402} />

            <h3 className="text-sm font-semibold mt-6 mb-3 uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.35)" }}>422 — Validation error</h3>
            <CodeBlock language="json" code={RESPONSE_422} />
          </Section>

          {/* Getting an API Key */}
          <Section id="get-key" title="Getting an API Key">
            <div className="space-y-4">
              {[
                { step: "01", title: "Contact your Kanal admin", body: "API keys are issued per courier tenant by the Kanal super-admin. Email your account manager or contact us at dev@cyrusfieldtech.com to request access for your integration." },
                { step: "02", title: "Key is minted on the Companies page", body: "The super-admin navigates to Companies → selects your courier → opens the Partner API Keys panel → clicks 'Generate key'. The plaintext key is shown exactly once at creation time." },
                { step: "03", title: "Store it securely", body: "Copy the key immediately and store it in your server environment variables (e.g., KANAL_API_KEY). It is never shown again — if lost, revoke and generate a new one." },
                { step: "04", title: "Start creating customers", body: "Use the key in the Authorization header as shown in the examples above. Each request creates a Kanal customer account scoped to your courier's tenant." },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex gap-4 p-4 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: "rgba(0,43,246,0.25)", color: "#60a5fa", border: "1px solid rgba(0,43,246,0.35)" }}>
                    {step}
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ color: "#f1f5f9" }}>{title}</p>
                    <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>{body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 rounded-xl" style={{ background: "rgba(0,43,246,0.1)", border: "1px solid rgba(0,43,246,0.3)" }}>
              <p className="text-sm font-semibold mb-1" style={{ color: "#93c5fd" }}>Questions or custom integrations?</p>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                Reach out at{" "}
                <a href="mailto:dev@cyrusfieldtech.com" className="underline" style={{ color: "#60a5fa" }}>dev@cyrusfieldtech.com</a>{" "}
                — we can help with server-side auth flows, webhooks, and white-label integrations.
              </p>
            </div>
          </Section>

          {/* Footer */}
          <footer className="mt-10 pt-6 text-center text-xs" style={{ borderTop: "1px solid rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.2)" }}>
            Kanal Partner API v1 &nbsp;·&nbsp; &copy; {new Date().getFullYear()} Cyrus Field Technologies
          </footer>

        </main>
      </div>
    </div>
  );
}
